import React, { useEffect, useRef, useState } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useValidator } from '../utils/useValidator';
import {
  getTaskCodes,
  createTaskCode,
  updateTaskCode,
  deleteTaskCode,
} from '../api/task-codeAPI';
import { getAssetSystems } from '../api/asset-systemAPI';

const validationFields = [
  { field: 'code', label: 'Code', required: true, maxLength: 25 },
  { field: 'name', label: 'Name', required: true, maxLength: 100 },
];

const TaskCodeListLayer = () => {
  const userProfile = useUserProfile();
  const [taskCodes, setTaskCodes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ code: '', name: '', systemId: '' });
  const [systemOptions, setSystemOptions] = useState([]);

  const { errors, setErrors, validate } = useValidator(validationFields, formData);

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchTaskCodesList = async () => {
    try {
      const response = await getTaskCodes();
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      setTaskCodes(list);
    } catch (error) {
      console.error('Error fetching task codes:', error);
    }
  };

  const fetchSystems = async () => {
    try {
      const response = await getAssetSystems();
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];

      const options = list.map((system) => {
        const label = `${system?.code ?? ''} ${system?.name ?? ''}`.trim();
        return {
          value: system?.id,
          label: label || `System ${system?.id ?? ''}`.trim(),
        };
      });

      setSystemOptions(options);
    } catch (error) {
      console.error('Error fetching systems:', error);
    }
  };

  useEffect(() => {
    fetchTaskCodesList();
    fetchSystems();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }

    if (taskCodes.length > 0 && tableRef.current) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }

    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [taskCodes]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'systemId') {
      setFormData((prev) => ({
        ...prev,
        [name]: value ? Number(value) : '',
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({ code: '', name: '', systemId: '' });
    setErrors({});
  };

  const handleAdd = () => {
    setFormData({ code: '', name: '', systemId: '' });
    setEditingId(null);
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setFormData({
      code: item?.code ?? '',
      name: item?.name ?? '',
      systemId: item?.systemId ?? item?.system?.id ?? '',
    });
    setEditingId(item?.id ?? null);
    setErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this task code?')) {
      return;
    }

    try {
      await deleteTaskCode(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchTaskCodesList();
    } catch (error) {
      console.error('Error deleting task code:', error);
    }
  };

  const handleSave = async () => {
    const isValid = validate();

    if (!isValid) {
      return;
    }

    const trimmedCode = formData.code?.toString().trim();
    const trimmedName = formData.name?.toString().trim();

    const payload = {
      code: trimmedCode,
      name: trimmedName,
      systemId: formData.systemId ? Number(formData.systemId) : null,
    };

    try {
      if (editingId) {
        await updateTaskCode(editingId, payload);
      } else {
        await createTaskCode(payload);
      }

      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }

      await fetchTaskCodesList();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving task code:', error);
    }
  };

  const getSystemLabel = (item) => {
    if (!item) {
      return '';
    }

    const system = item.system;

    if (system) {
      return `${system.code ?? ''} ${system.name ?? ''}`.trim();
    }

    if (item.systemId) {
      const fallback = systemOptions.find((option) => option.value === item.systemId);
      return fallback?.label ?? '';
    }

    return '';
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {userProfile?.privileges?.includes('tasks:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={handleAdd}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Task Code
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th style={{ textAlign: "left"}}>Code</th>
                <th>Name</th>
                <th>System</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {taskCodes.map((item) => (
                <tr key={item.id}>
                  <td style={{ textAlign: "left"}}><strong>{item.code}</strong></td>
                  <td><strong>{item.name}</strong></td>
                  <td>
                    <strong>
                      {getSystemLabel(item)}
                    </strong>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('tasks:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(item)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('tasks:delete') && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Task Code' : 'New Task Code'}</h5>
                <button type="button" className="btn-close" onClick={handleCloseModal} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Code *</label>
                  <input
                    name="code"
                    className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                    value={formData.code}
                    onChange={handleInputChange}
                  />
                  {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                </div>
                <div className="mb-3">
                  <label className="form-label">Name *</label>
                  <input
                    name="name"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>
                <div className="mb-3">
                  <label className="form-label">System</label>
                  <select
                    name="systemId"
                    className="form-select"
                    value={
                      formData.systemId !== null && formData.systemId !== undefined && formData.systemId !== ''
                        ? String(formData.systemId)
                        : ''
                    }
                    onChange={handleInputChange}
                  >
                    <option value="">Select System</option>
                    {systemOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={handleCloseModal}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskCodeListLayer;
